import { useState } from 'react';
import type { JSX } from 'react';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { FeedbackPalette } from '../../types.js';

interface ThumbsRatingProps {
  palette: FeedbackPalette;
  value: number | null;
  onChange: (rating: number) => void;
}

const RATING_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

// Visible thumbs-up / thumbs-down SVGs. Labels match the icon (used as
// aria-label and screen-reader text) so "rating: 'thumbs'" exposes a
// "Thumbs up" / "Thumbs down" control, not the prior misleading "Yes" / "No".
const THUMBS_OPTIONS: { value: number; label: string; path: string }[] = [
  {
    value: 1,
    label: 'Thumbs up',
    path: 'M2 21h4V9H2v12zM23 10c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z',
  },
  {
    value: 0,
    label: 'Thumbs down',
    path: 'M22 3h-4v12h4V3zM1 14c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2H6c-.83 0-1.54.5-1.84 1.22L1.14 11.27c-.09.23-.14.47-.14.73v2z',
  },
];

export function ThumbsRating({ palette, value, onChange }: ThumbsRatingProps): JSX.Element {
  const [hovered, setHovered] = useState<number | null>(null);

  return (
    <div
      style={{ display: 'flex', gap: 18, justifyContent: 'center', padding: '8px 0' }}
      onMouseLeave={() => setHovered(null)}
    >
      {THUMBS_OPTIONS.map((option) => {
        const selected = value === option.value;
        const isHovered = hovered === option.value && !selected;
        const borderColor = selected
          ? palette.primary
          : isHovered
            ? hexWithAlpha(palette.primary, 0.6)
            : palette.border;
        const background = selected
          ? palette.primarySoft
          : isHovered
            ? hexWithAlpha(palette.primary, 0.06)
            : 'transparent';
        const color = selected
          ? palette.primary
          : isHovered
            ? palette.primary
            : palette.textMuted;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            onMouseEnter={() => setHovered(option.value)}
            aria-label={option.label}
            style={{
              background,
              border: `1.5px solid ${borderColor}`,
              borderRadius: 14,
              padding: 14,
              cursor: 'pointer',
              color,
              transform: isHovered ? 'translateY(-2px)' : 'translateY(0)',
              transition: `border-color 220ms ${RATING_EASE}, background-color 220ms ${RATING_EASE}, color 220ms ${RATING_EASE}, transform 220ms ${RATING_EASE}`,
            }}
          >
            <svg viewBox="0 0 24 24" width="34" height="34" fill="currentColor">
              <path d={option.path} />
            </svg>
          </button>
        );
      })}
    </div>
  );
}
